// @wc-ignore-file
// (error codes, RPC names, bucket options and error codes are data, not copy.)

/**
 * CR-235 — one suggestion's page, for its submitter (a contributor).
 *
 * Every read and write goes through the definer RPCs of
 * 20261021120000_cr235_suggestion_attachments.sql: the contributor is not a
 * project member, so `cr_suggestions` and `cr_suggestion_attachments` RLS
 * refuse them direct access. None of these answers carries the CR id.
 *
 * Files follow the browser → Storage → RPC shape of CR-147
 * (`uploadToCrUploadsBucket`), in the suggestion bucket and with its rules.
 */
import { sanitizeFileName } from "@/features/attachments/attachments.utils";
import { supabase } from "@/lib/supabase";
import * as Sentry from "@sentry/react";
import type {
  CrSuggestionAttachment,
  CrSuggestionClassification,
  MyCrSuggestionAttachment,
  MyCrSuggestionDetail,
} from "@shared/types/supabase.types";
import {
  CR_SUGGESTION_UPLOAD_BUCKET,
  CR_SUGGESTION_UPLOAD_RULES,
  isCrSuggestionUploadInline,
} from "../cr-suggestion-uploads";
import { FileTooLargeError, UnsupportedFileError } from "../cr-uploads";
import { uploadToCrUploadsBucket } from "./cr-uploads-storage";

/** The caller's suggestion, or `null` when the id is unknown or not theirs. */
export async function getMyCrSuggestion(
  suggestionId: string,
): Promise<MyCrSuggestionDetail | null> {
  const { data, error } = await supabase.rpc("get_my_cr_suggestion", {
    p_suggestion_id: suggestionId,
  });
  // 22P02: the id in the URL is not a uuid, so there is no such suggestion.
  if (error?.code === "22P02") {
    return null;
  }
  if (error) {
    throw new Error(`Failed to read your suggestion: ${error.message}`);
  }
  return data?.[0] ?? null;
}

export type UpdateMyCrSuggestionInput = {
  title: string;
  description: string;
  classification: CrSuggestionClassification;
};

/**
 * Save the three editable fields (the RPC logs one `suggestion_updated` event
 * only when something changed). The raw PostgREST error is rethrown so the
 * page maps its error code with `crSuggestionWriteErrorCode`.
 */
export async function updateMyCrSuggestion(
  suggestionId: string,
  input: UpdateMyCrSuggestionInput,
): Promise<MyCrSuggestionDetail> {
  const { data, error } = await supabase.rpc("update_my_cr_suggestion", {
    p_suggestion_id: suggestionId,
    p_title: input.title,
    p_description: input.description,
    p_classification: input.classification,
  });
  if (error) {
    throw error;
  }
  const row = data?.[0];
  if (!row) {
    throw new Error("update_my_cr_suggestion returned no row");
  }
  return row;
}

/** Upload one file under `{suggestionId}/{uuid}-{name}` and attach it. */
export async function addMyCrSuggestionAttachment(
  suggestionId: string,
  file: File,
): Promise<CrSuggestionAttachment> {
  return uploadToCrUploadsBucket({
    prefixId: suggestionId,
    file,
    rules: CR_SUGGESTION_UPLOAD_RULES,
    link: (storagePath) =>
      supabase.rpc("add_my_cr_suggestion_attachment", {
        p_suggestion_id: suggestionId,
        p_storage_path: storagePath,
      }),
  });
}

/**
 * Detach a file: the RPC first (the DELETE policy only reaches an object no
 * attachment row names any more), then the bytes, best effort — once the row
 * is gone the file is gone for everyone, so a Storage failure is reported to
 * Sentry rather than shown (same reasoning as CR-147's removal).
 */
export async function removeMyCrSuggestionAttachment(attachmentId: string): Promise<void> {
  const { data, error } = await supabase.rpc("remove_my_cr_suggestion_attachment", {
    p_attachment_id: attachmentId,
  });
  if (error) {
    throw error;
  }
  if (!data) {
    return;
  }
  const { error: removeError } = await supabase.storage
    .from(CR_SUGGESTION_UPLOAD_BUCKET)
    .remove([data]);
  if (removeError) {
    Sentry.captureException(removeError, {
      tags: { feature: "cr-suggestion-uploads" },
      extra: { storagePath: data },
    });
  }
}

/**
 * A 60 s link to the original bytes (the bucket is private). Raster images
 * render in the tab; PDFs and videos download under their sanitised name
 * (CR-147: the MIME label is the uploader's browser's, and storage-js appends
 * the name unencoded).
 */
export async function createCrSuggestionAttachmentSignedUrl(
  attachment: Pick<MyCrSuggestionAttachment, "storage_path" | "file_name" | "mime_type">,
): Promise<string> {
  const options = isCrSuggestionUploadInline(attachment.mime_type)
    ? undefined
    : { download: sanitizeFileName(attachment.file_name) };
  const { data, error } = await supabase.storage
    .from(CR_SUGGESTION_UPLOAD_BUCKET)
    .createSignedUrl(attachment.storage_path, 60, options);
  if (error) {
    throw error;
  }
  return data.signedUrl;
}

/** Why a write on the suggestion page failed, for the product message. */
export type CrSuggestionWriteErrorCode =
  | "not_found"
  | "closed"
  | "rate_limited"
  | "too_many_files"
  | "too_large"
  | "unsupported"
  | "invalid"
  | "unknown";

/**
 * Map a failure to a code. error codes come from the CR-235 RPCs (42501 not
 * the caller's, P0409 closed, P0429 hourly cap, 22023 bad input — told apart
 * by their fixed messages); the file errors from the client-side check.
 * Storage-policy refusals reach the browser as HTTP errors: `unknown`.
 */
export function crSuggestionWriteErrorCode(error: unknown): CrSuggestionWriteErrorCode {
  if (error instanceof UnsupportedFileError) return "unsupported";
  if (error instanceof FileTooLargeError) return "too_large";
  if (!error || typeof error !== "object") return "unknown";
  const { code, message } = error as { code?: unknown; message?: unknown };
  const text = typeof message === "string" ? message : "";
  switch (code) {
    case "42501":
      return "not_found";
    case "P0409":
      return "closed";
    case "P0429":
      return "rate_limited";
    case "22023":
      if (text.startsWith("too many files")) return "too_many_files";
      if (text.startsWith("file size")) return "too_large";
      if (text.startsWith("file type")) return "unsupported";
      return "invalid";
    default:
      return "unknown";
  }
}

/** One file's fate in a batch upload. */
export type CrSuggestionFileUploadResult = {
  file: File;
  error: CrSuggestionWriteErrorCode | null;
};

/**
 * CR-235 D7/Q5: attach files one by one, after the suggestion exists — the
 * one batch loop behind the dialog and the suggestion page. A failure never
 * stops the batch nor undoes the suggestion; each file reports its own
 * outcome. `upload` lets the page route each file through its mutation (cache
 * invalidation), or the Feedback dialog route it to a change request (CR-296;
 * `suggestionId` is then only a label: the default `upload` alone reads it);
 * `onFileStart` / `onFileSettled` drive the per-file state.
 */
export async function uploadCrSuggestionFiles(
  suggestionId: string,
  files: readonly File[],
  {
    onFileStart,
    onFileSettled,
    upload = (file) => addMyCrSuggestionAttachment(suggestionId, file),
  }: {
    onFileStart?: (index: number) => void;
    onFileSettled?: (index: number, result: CrSuggestionFileUploadResult) => void;
    upload?: (file: File) => Promise<unknown>;
  } = {},
): Promise<CrSuggestionFileUploadResult[]> {
  const results: CrSuggestionFileUploadResult[] = [];
  for (const [index, file] of files.entries()) {
    onFileStart?.(index);
    let result: CrSuggestionFileUploadResult;
    try {
      await upload(file);
      result = { file, error: null };
    } catch (error) {
      result = { file, error: crSuggestionWriteErrorCode(error) };
    }
    results.push(result);
    onFileSettled?.(index, result);
  }
  return results;
}
